import { useEffect } from 'react';
import { usePlaybackStore } from '@/stores';
import type { SkipContext } from '@/stores/playbackStore';

/** OTO-027: fixed transport-skip amount for the arrow-key shortcuts. */
const SKIP_SECONDS = 5;

interface UsePlaybackShortcutsOptions {
  skipContext: SkipContext;
  /** No-ops the shortcuts while audio isn't loaded yet, mirroring the skip
   *  buttons' own `disabled={!audioLoaded}` binding. `audioLoaded` is a
   *  component prop (from the `pianoLoadState` hook), not store state, so
   *  it must arrive here as a parameter -- `usePlaybackStore.getState()`
   *  cannot see it. */
  audioLoaded: boolean;
}

/**
 * OTO-027: arrow-key transport-skip shortcuts (Left = -5s, Right = +5s),
 * scoped to whichever view mounts this hook (registers/cleans up its own
 * `window` keydown listener on mount/unmount). Guards:
 * - Modifier keys (meta/ctrl/alt) no-op, avoiding collisions with OS/browser
 *   shortcuts (e.g. Alt+Left history navigation).
 * - Editable/range-input focus targets no-op WITHOUT calling
 *   preventDefault, so native behavior (e.g. the tempo slider's own
 *   arrow-key value change) is preserved.
 * - Dispatches via `usePlaybackStore.getState().skipBy(...)` for a fresh
 *   action reference on every keypress, never a stale closure.
 */
export function usePlaybackShortcuts({
  skipContext,
  audioLoaded,
}: UsePlaybackShortcutsOptions): void {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!audioLoaded) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        usePlaybackStore.getState().skipBy(-SKIP_SECONDS, skipContext);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        usePlaybackStore.getState().skipBy(SKIP_SECONDS, skipContext);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [skipContext, audioLoaded]);
}
